/**
 * Registr typů uzlů — JEDINÝ zdroj pravdy pro paletu (klient), validaci spojení
 * (klient i server) a kompilaci/runtime (server). Deklarativní: porty, zod schéma
 * parametrů, kategorie, ikona, klíče do dictionary. ŽÁDNÉ server/klient importy.
 *
 * Server dodává evaluate() v services/automations/nodes/, klient komponenty v
 * components/automations/nodes/ — oba se klíčují podle těchto typů.
 */

import { z } from "zod";
import { MAX_EXPRESSION_LENGTH } from "@globals/rules";
import type {
  AutomationNode,
  AutomationNodeType,
  DataType,
  HandleSpec,
  NodeRegistryEntry,
} from "@globals/automations/types";

/**
 * Minimum, které `resolveNodeHandles` potřebuje o hranách vědět. Záměrně volnější než
 * `AutomationEdge`, aby šlo předat i hrany z React Flow (tam je `targetHandle` nullable)
 * bez konverze na obou stranách.
 */
export type EdgeLike = { target: string; targetHandle?: string | null };

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Společné schéma device-action uzlů (PIP/Tracer/BMS). `field` validuje engine proti DEVICE_ACTION_PARAMS. */
const deviceActionSchema = z.object({
  slug: z.string().min(1),
  field: z.string().min(1),
  valueMode: z.enum(["const", "input"]).default("const"),
  constValue: z.union([z.number(), z.string(), z.boolean()]).optional(),
});

/** Per-type zod schéma parametrů. Drženo mimo NodeRegistryEntry, ať types.ts nezávisí na zodu. */
export const NODE_PARAM_SCHEMAS: Record<AutomationNodeType, z.ZodTypeAny> = {
  // A) Spouštěče
  "trigger.topicChange": z.object({
    topicId: z.string().min(1),
    mode: z.enum(["any", "rising", "falling", "deltaMin"]).default("any"),
    deltaMin: z.number().positive().optional(),
  }),
  "trigger.time": z.object({
    time: z.string().regex(HHMM),
    daysOfWeek: z.number().int().min(1).max(127).default(127),
    missedPolicy: z.enum(["skip", "fireLate"]).default("skip"),
  }),
  "trigger.interval": z.object({
    everySeconds: z.number().int().min(10).max(604_800),
    align: z.enum(["none", "minute", "hour"]).default("none"),
  }),
  "trigger.manual": z.object({ label: z.string().max(64).default("Spustit") }),

  // B) Hodnoty
  "value.topic": z.object({ topicId: z.string().min(1) }),
  "value.lastDb": z.object({ topicId: z.string().min(1) }),
  "value.const": z.object({
    valueType: z.enum(["number", "boolean", "string"]),
    value: z.union([z.number(), z.boolean(), z.string()]),
  }),
  "value.age": z.object({ topicId: z.string().min(1) }),

  // C) Logika
  "logic.compare": z.object({
    operator: z.enum([">", "<", ">=", "<=", "==", "!="]),
    bMode: z.enum(["input", "const"]).default("input"),
    bConst: z.number().optional(),
    tolerance: z.number().min(0).default(0),
  }),
  "logic.and": z.object({ arity: z.number().int().min(2).max(8).default(2) }),
  "logic.or": z.object({ arity: z.number().int().min(2).max(8).default(2) }),
  "logic.not": z.object({}),
  "logic.hysteresis": z.object({
    direction: z.enum(["aboveOn", "belowOn"]).default("aboveOn"),
    onValue: z.number(),
    offValue: z.number(),
    initial: z.boolean().default(false),
  }),

  // D) Matematika
  "math.op": z.object({
    op: z.enum(["+", "-", "*", "/", "%", "^"]),
    bMode: z.enum(["input", "const"]).default("input"),
    bConst: z.number().optional(),
  }),
  "math.deltaOverTime": z.object({
    topicId: z.string().min(1),
    windowSeconds: z.number().int().min(1).max(604_800),
    direction: z.enum(["any", "rise", "fall"]).default("any"),
  }),
  "math.formula": z.object({
    expression: z.string().min(1).max(MAX_EXPRESSION_LENGTH),
    inputs: z
      .array(z.string().regex(/^[a-h]$/))
      .max(8)
      .default([]),
    outType: z.enum(["number", "boolean"]).default("boolean"),
  }),

  // E) Čas / tok
  "time.sustained": z.object({ seconds: z.number().int().min(1).max(604_800) }),
  "time.window": z.object({
    from: z.string().regex(HHMM),
    to: z.string().regex(HHMM),
    daysOfWeek: z.number().int().min(1).max(127).default(127),
  }),
  "time.gate": z.object({}),
  "flow.risingEdge": z.object({}),
  "flow.throttle": z.object({ seconds: z.number().int().min(1).max(604_800) }),
  "time.delay": z.object({
    seconds: z.number().int().min(1).max(604_800),
    mode: z.enum(["restart", "ignore", "queue"]).default("restart"),
    missedPolicy: z.enum(["fire", "skip"]).default("fire"),
  }),

  // F) Akce
  "action.notify": z.object({
    severity: z.enum(["INFO", "WARNING", "SERIOUS"]).default("INFO"),
    title: z.string().max(255).default(""),
    body: z.string().max(1000).default(""),
    cooldownSeconds: z.number().int().min(0).default(0),
    valueInputs: z.array(z.string().min(1).max(40)).max(8).default([]),
  }),
  "action.log": z.object({
    message: z.string().max(500).default(""),
    valueInputs: z.array(z.string().min(1).max(40)).max(8).default([]),
  }),

  // F2) Akce nad zařízeními. `field` validuje engine proti DEVICE_ACTION_PARAMS;
  // hodnota je buď konstanta (`constValue`), nebo přijde signálem na vstup `value`.
  "action.setPipParam": deviceActionSchema,
  "action.setTracerParam": deviceActionSchema,
  "action.setBmsParam": deviceActionSchema,
  "action.publishMqtt": z.object({
    topic: z.string().min(1).max(255),
    valueMode: z.enum(["const", "input"]).default("const"),
    constValue: z.union([z.number(), z.string(), z.boolean()]).optional(),
    retain: z.boolean().default(false),
  }),
};

const pulseOut: HandleSpec = { id: "pulse", dataType: "pulse", labelKey: "automations.handles.pulse" };
const valueOut = (dataType: DataType): HandleSpec => ({ id: "out", dataType, labelKey: "automations.handles.value" });

export const NODE_REGISTRY: Record<AutomationNodeType, NodeRegistryEntry> = {
  // A) Spouštěče
  "trigger.topicChange": {
    type: "trigger.topicChange",
    category: "trigger",
    phase: 1,
    icon: "Activity",
    labelKey: "automations.nodes.topicChange.label",
    descriptionKey: "automations.nodes.topicChange.desc",
    inputs: [],
    outputs: [pulseOut],
  },
  "trigger.time": {
    type: "trigger.time",
    category: "trigger",
    phase: 1,
    icon: "Clock",
    stateful: { durable: true },
    labelKey: "automations.nodes.time.label",
    descriptionKey: "automations.nodes.time.desc",
    inputs: [],
    outputs: [pulseOut],
  },
  "trigger.interval": {
    type: "trigger.interval",
    category: "trigger",
    phase: 1,
    icon: "Timer",
    stateful: { durable: true },
    labelKey: "automations.nodes.interval.label",
    descriptionKey: "automations.nodes.interval.desc",
    inputs: [],
    outputs: [pulseOut],
  },
  "trigger.manual": {
    type: "trigger.manual",
    category: "trigger",
    phase: 1,
    icon: "MousePointerClick",
    labelKey: "automations.nodes.manual.label",
    descriptionKey: "automations.nodes.manual.desc",
    inputs: [],
    outputs: [pulseOut],
  },

  // B) Hodnoty
  "value.topic": {
    type: "value.topic",
    category: "value",
    phase: 1,
    icon: "Gauge",
    labelKey: "automations.nodes.topic.label",
    descriptionKey: "automations.nodes.topic.desc",
    inputs: [],
    outputs: [valueOut("any")],
  },
  "value.lastDb": {
    type: "value.lastDb",
    category: "value",
    phase: 1,
    icon: "Database",
    labelKey: "automations.nodes.lastDb.label",
    descriptionKey: "automations.nodes.lastDb.desc",
    inputs: [],
    outputs: [valueOut("any")],
  },
  "value.const": {
    type: "value.const",
    category: "value",
    phase: 1,
    icon: "Hash",
    labelKey: "automations.nodes.const.label",
    descriptionKey: "automations.nodes.const.desc",
    inputs: [],
    outputs: [valueOut("any")],
  },
  "value.age": {
    type: "value.age",
    category: "value",
    phase: 1,
    icon: "History",
    labelKey: "automations.nodes.age.label",
    descriptionKey: "automations.nodes.age.desc",
    inputs: [],
    outputs: [valueOut("number")],
  },

  // C) Logika
  "logic.compare": {
    type: "logic.compare",
    category: "logic",
    phase: 1,
    icon: "Scale",
    labelKey: "automations.nodes.compare.label",
    descriptionKey: "automations.nodes.compare.desc",
    inputs: [
      { id: "a", dataType: "number", labelKey: "automations.handles.a" },
      { id: "b", dataType: "number", labelKey: "automations.handles.b", optional: true },
    ],
    outputs: [valueOut("boolean")],
  },
  "logic.and": {
    type: "logic.and",
    category: "logic",
    phase: 1,
    icon: "Combine",
    labelKey: "automations.nodes.and.label",
    descriptionKey: "automations.nodes.and.desc",
    inputs: [{ id: "in", dataType: "boolean", variadic: true, labelKey: "automations.handles.in" }],
    outputs: [valueOut("boolean")],
  },
  "logic.or": {
    type: "logic.or",
    category: "logic",
    phase: 1,
    icon: "Split",
    labelKey: "automations.nodes.or.label",
    descriptionKey: "automations.nodes.or.desc",
    inputs: [{ id: "in", dataType: "boolean", variadic: true, labelKey: "automations.handles.in" }],
    outputs: [valueOut("boolean")],
  },
  "logic.not": {
    type: "logic.not",
    category: "logic",
    phase: 1,
    icon: "Ban",
    labelKey: "automations.nodes.not.label",
    descriptionKey: "automations.nodes.not.desc",
    inputs: [{ id: "a", dataType: "boolean", labelKey: "automations.handles.cond" }],
    outputs: [valueOut("boolean")],
  },
  "logic.hysteresis": {
    type: "logic.hysteresis",
    category: "logic",
    phase: 1,
    icon: "ToggleRight",
    stateful: { durable: true },
    labelKey: "automations.nodes.hysteresis.label",
    descriptionKey: "automations.nodes.hysteresis.desc",
    inputs: [{ id: "a", dataType: "number", labelKey: "automations.handles.a" }],
    outputs: [valueOut("boolean")],
  },

  // D) Matematika
  "math.op": {
    type: "math.op",
    category: "math",
    phase: 1,
    icon: "Calculator",
    labelKey: "automations.nodes.mathOp.label",
    descriptionKey: "automations.nodes.mathOp.desc",
    inputs: [
      { id: "a", dataType: "number", labelKey: "automations.handles.a" },
      { id: "b", dataType: "number", labelKey: "automations.handles.b", optional: true },
    ],
    outputs: [valueOut("number")],
  },
  "math.deltaOverTime": {
    type: "math.deltaOverTime",
    category: "math",
    phase: 1,
    icon: "Diff",
    labelKey: "automations.nodes.delta.label",
    descriptionKey: "automations.nodes.delta.desc",
    inputs: [],
    outputs: [valueOut("number")],
  },
  "math.formula": {
    type: "math.formula",
    category: "math",
    phase: 1,
    icon: "FunctionSquare",
    labelKey: "automations.nodes.formula.label",
    descriptionKey: "automations.nodes.formula.desc",
    inputs: [],
    outputs: [valueOut("any")],
  },

  // E) Čas / tok
  "time.sustained": {
    type: "time.sustained",
    category: "time",
    phase: 1,
    icon: "Hourglass",
    stateful: { durable: true },
    labelKey: "automations.nodes.sustained.label",
    descriptionKey: "automations.nodes.sustained.desc",
    inputs: [{ id: "a", dataType: "boolean", labelKey: "automations.handles.cond" }],
    outputs: [valueOut("boolean")],
  },
  "time.window": {
    type: "time.window",
    category: "time",
    phase: 1,
    icon: "CalendarRange",
    labelKey: "automations.nodes.window.label",
    descriptionKey: "automations.nodes.window.desc",
    inputs: [],
    outputs: [valueOut("boolean")],
  },
  "time.gate": {
    type: "time.gate",
    category: "time",
    phase: 1,
    icon: "DoorOpen",
    labelKey: "automations.nodes.gate.label",
    descriptionKey: "automations.nodes.gate.desc",
    inputs: [
      { id: "pulse", dataType: "pulse", labelKey: "automations.handles.pulse" },
      { id: "cond", dataType: "boolean", labelKey: "automations.handles.cond" },
    ],
    outputs: [pulseOut],
  },
  "flow.risingEdge": {
    type: "flow.risingEdge",
    category: "flow",
    phase: 1,
    icon: "Zap",
    stateful: { durable: true },
    labelKey: "automations.nodes.risingEdge.label",
    descriptionKey: "automations.nodes.risingEdge.desc",
    inputs: [{ id: "a", dataType: "boolean", labelKey: "automations.handles.cond" }],
    outputs: [
      { id: "rising", dataType: "pulse", labelKey: "automations.handles.rising" },
      { id: "falling", dataType: "pulse", labelKey: "automations.handles.falling" },
    ],
  },
  "flow.throttle": {
    type: "flow.throttle",
    category: "flow",
    phase: 1,
    icon: "Filter",
    stateful: { durable: true },
    labelKey: "automations.nodes.throttle.label",
    descriptionKey: "automations.nodes.throttle.desc",
    inputs: [{ id: "pulse", dataType: "pulse", labelKey: "automations.handles.pulse" }],
    outputs: [pulseOut],
  },
  "time.delay": {
    type: "time.delay",
    category: "time",
    phase: 1,
    icon: "TimerReset",
    stateful: { durable: true },
    labelKey: "automations.nodes.delay.label",
    descriptionKey: "automations.nodes.delay.desc",
    inputs: [{ id: "pulse", dataType: "pulse", labelKey: "automations.handles.pulse" }],
    outputs: [pulseOut],
  },

  // F) Akce
  "action.notify": {
    type: "action.notify",
    category: "action",
    phase: 1,
    icon: "Bell",
    labelKey: "automations.nodes.notify.label",
    descriptionKey: "automations.nodes.notify.desc",
    inputs: [{ id: "run", dataType: "pulse", labelKey: "automations.handles.run" }],
    outputs: [],
  },
  "action.log": {
    type: "action.log",
    category: "action",
    phase: 1,
    icon: "FileText",
    labelKey: "automations.nodes.log.label",
    descriptionKey: "automations.nodes.log.desc",
    inputs: [{ id: "run", dataType: "pulse", labelKey: "automations.handles.run" }],
    outputs: [],
  },

  // F2) Akce nad zařízeními (privilegované — ADMIN). Vstup `value` se objeví jen při valueMode=input.
  "action.setPipParam": {
    type: "action.setPipParam",
    category: "action",
    phase: 2,
    icon: "Cpu",
    stateful: { durable: true },
    labelKey: "automations.nodes.setPipParam.label",
    descriptionKey: "automations.nodes.setPipParam.desc",
    inputs: [{ id: "run", dataType: "pulse", labelKey: "automations.handles.run" }],
    outputs: [],
  },
  "action.setTracerParam": {
    type: "action.setTracerParam",
    category: "action",
    phase: 2,
    icon: "Cpu",
    stateful: { durable: true },
    labelKey: "automations.nodes.setTracerParam.label",
    descriptionKey: "automations.nodes.setTracerParam.desc",
    inputs: [{ id: "run", dataType: "pulse", labelKey: "automations.handles.run" }],
    outputs: [],
  },
  "action.setBmsParam": {
    type: "action.setBmsParam",
    category: "action",
    phase: 2,
    icon: "Cpu",
    stateful: { durable: true },
    labelKey: "automations.nodes.setBmsParam.label",
    descriptionKey: "automations.nodes.setBmsParam.desc",
    inputs: [{ id: "run", dataType: "pulse", labelKey: "automations.handles.run" }],
    outputs: [],
  },
  "action.publishMqtt": {
    type: "action.publishMqtt",
    category: "action",
    phase: 2,
    icon: "Radio",
    stateful: { durable: true },
    labelKey: "automations.nodes.publishMqtt.label",
    descriptionKey: "automations.nodes.publishMqtt.desc",
    inputs: [{ id: "run", dataType: "pulse", labelKey: "automations.handles.run" }],
    outputs: [],
  },
};

/** Vrátí registr záznam nebo undefined (neznámý typ). */
export const getNodeEntry = (type: string): NodeRegistryEntry | undefined =>
  (NODE_REGISTRY as Record<string, NodeRegistryEntry>)[type];

/** Bezpečně přečte number param (po zod parse jsou typy jisté, ale graf z DB může být cokoli). */
const numParam = (node: AutomationNode, key: string, fallback: number): number => {
  const v = node.params[key];
  return typeof v === "number" ? v : fallback;
};

/**
 * Počet vstupů variadického uzlu (A zároveň / Nebo). Uzel „roste" — má vždy o jeden
 * volný port víc, než kolik jich je zapojeno (min 2, max 8). `arity` v params je jen
 * fallback pro kontexty bez hran (a zpětná kompatibilita starších grafů).
 */
export const variadicArity = (node: AutomationNode, edges?: EdgeLike[]): number => {
  const fallback = Math.min(8, Math.max(2, numParam(node, "arity", 2)));
  if (!edges) return fallback;
  let highest = -1;
  for (const e of edges) {
    if (e.target !== node.id || !e.targetHandle) continue;
    const m = /^in-(\d+)$/.exec(e.targetHandle);
    if (m) highest = Math.max(highest, Number(m[1]));
  }
  return Math.min(8, Math.max(2, highest + 2));
};

/**
 * Konkrétní handly uzlu po zohlednění parametrů (variadic arity, pojmenované vstupy,
 * bMode=const → bez „b" vstupu, typ konstanty/výstupu). Validace i runtime z toho čtou.
 *
 * `edges` je nepovinný — s ním variadické uzly (A zároveň / Nebo) automaticky rostou
 * podle zapojení; bez něj se použije `params.arity`.
 */
export const resolveNodeHandles = (
  node: AutomationNode,
  edges?: EdgeLike[]
): { inputs: HandleSpec[]; outputs: HandleSpec[] } => {
  const entry = getNodeEntry(node.type);
  if (!entry) return { inputs: [], outputs: [] };

  switch (node.type) {
    case "logic.and":
    case "logic.or": {
      const arity = variadicArity(node, edges);
      return {
        // Jednotlivé porty jsou nepovinné (uzel roste, mezery po smazané hraně nevadí);
        // požadavek „aspoň 2 zapojené" hlídá validateGraph zvlášť (VARIADIC_TOO_FEW).
        inputs: Array.from({ length: arity }, (_, i) => ({
          id: `in-${i}`,
          dataType: "boolean" as DataType,
          labelKey: "automations.handles.in",
          optional: true,
          hint: "Uzel roste sám — zapoj kolik potřebuješ, ale aspoň dva vstupy.",
        })),
        outputs: entry.outputs,
      };
    }
    case "math.formula": {
      const inputs = Array.isArray(node.params.inputs) ? (node.params.inputs as string[]) : [];
      const outType =
        node.params.outType === "number" ? "number" : node.params.outType === "boolean" ? "boolean" : "any";
      return {
        inputs: inputs.map((name) => ({
          id: `in-${name}`,
          dataType: "any" as DataType,
          labelKey: "automations.handles.named",
        })),
        outputs: [valueOut(outType as DataType)],
      };
    }
    case "value.const": {
      const vt = node.params.valueType;
      const dt: DataType = vt === "number" || vt === "boolean" || vt === "string" ? vt : "any";
      return { inputs: [], outputs: [valueOut(dt)] };
    }
    case "logic.compare":
    case "math.op": {
      const inputs = node.params.bMode === "const" ? entry.inputs.filter((h) => h.id !== "b") : entry.inputs;
      return { inputs, outputs: entry.outputs };
    }
    case "action.notify":
    case "action.log": {
      const names = Array.isArray(node.params.valueInputs) ? (node.params.valueInputs as string[]) : [];
      return {
        inputs: [
          { id: "run", dataType: "pulse", labelKey: "automations.handles.run" },
          ...names.map((n) => ({
            id: `val-${n}`,
            dataType: "any" as DataType,
            labelKey: "automations.handles.named",
            optional: true,
          })),
        ],
        outputs: [],
      };
    }
    case "action.setPipParam":
    case "action.setTracerParam":
    case "action.setBmsParam":
    case "action.publishMqtt": {
      const fromInput = node.params.valueMode === "input";
      return {
        inputs: [
          { id: "run", dataType: "pulse", labelKey: "automations.handles.run" },
          ...(fromInput ? [{ id: "value", dataType: "any" as DataType, labelKey: "automations.handles.value" }] : []),
        ],
        outputs: [],
      };
    }
    default:
      return { inputs: entry.inputs, outputs: entry.outputs };
  }
};

/**
 * Kompatibilita typů hrany (výstup → vstup). Striktní s výjimkami:
 * - `any` se snese se vším,
 * - `boolean → pulse` = implicitní „při splnění" adaptér (událost ve chvíli, kdy podmínka začne platit),
 * - `pulse → boolean` = bezstavová koerce (událost „platí" právě v okamžiku, kdy nastala).
 *
 * Díky obousměrnosti se dají spouštěče i podmínky libovolně míchat v uzlech
 * Nebo / A zároveň / Opak / Brána — uživatel nemusí znát rozdíl mezi událostí a stavem.
 * Zbylé odmítnuté kombinace jsou jen ty s číslem/textem, kde je jediná správná rada
 * „vlož Porovnání" (editor ji nabízí jako quick-fix).
 */
export const typesCompatible = (out: DataType, inp: DataType): boolean => {
  if (out === inp) return true;
  if (out === "any" || inp === "any") return true;
  if (out === "boolean" && inp === "pulse") return true;
  if (out === "pulse" && inp === "boolean") return true;
  return false;
};
